import {useEffect, useRef, useState} from 'react';
import type {AssetRetryCacheBusting} from './social-post-content-media-wrapper.types';
import {ASSET_RETRY_TIMEOUT_MS, MAX_ASSET_RETRIES} from './social-post-content-media-wrapper.types';

export function useAssetRetryCacheBusting(): AssetRetryCacheBusting {
  const [retryThroughRefreshNumber, setRetryThroughRefreshNumber] = useState(0);
  const [isCacheBustingRequested, setIsCacheBustingRequested] = useState(false);
  const pendingRetryTimeoutIds = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect((): VoidFunction => {
    const scheduledRetryTimeoutIds = pendingRetryTimeoutIds.current;

    return (): void => {
      scheduledRetryTimeoutIds.forEach(clearTimeout);
      scheduledRetryTimeoutIds.clear();
    };
  }, []);

  const areMaxRetriesReached = retryThroughRefreshNumber >= MAX_ASSET_RETRIES;

  const scheduleRetry = (): void => {
    if (areMaxRetriesReached) {
      return;
    }

    setIsCacheBustingRequested(true);

    const retryTimeoutId = setTimeout((): void => {
      pendingRetryTimeoutIds.current.delete(retryTimeoutId);
      setRetryThroughRefreshNumber((previousRetryNumber) => Math.min(previousRetryNumber + 1, MAX_ASSET_RETRIES));
    }, ASSET_RETRY_TIMEOUT_MS);

    pendingRetryTimeoutIds.current.add(retryTimeoutId);
  };

  const stopCacheBusting = (): void => {
    setIsCacheBustingRequested(false);
  };

  return {
    isCacheBustingNeeded: isCacheBustingRequested && !areMaxRetriesReached,
    scheduleRetry: scheduleRetry,
    stopCacheBusting: stopCacheBusting,
  };
}
